/**
Copyright (C) 2022 by SeQuenX  - All Rights Reserved
This file is part of the ComplyVantage product development,
and is released under the "Commercial License Agreement".
You should have received a copy of the Commercial License Agreement license with
this file. If not, please write to: legal@sequenx.com, or visit www.sequenx.com
*/
import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

const RecruitmentDetails = () => {
  const { t } = useTranslation();

  const processSteps = [
    {
      title: t("recruitment.process.steps.understanding.title"),
      description: t("recruitment.process.steps.understanding.description"),
      icon: "🎯",
      color: "from-slate-700 to-slate-800",
    },
    {
      title: t("recruitment.process.steps.recruitment.title"),
      description: t("recruitment.process.steps.recruitment.description"),
      icon: "👥",
      color: "from-slate-600 to-slate-700",
    },
    {
      title: t("recruitment.process.steps.matchmaking.title"),
      description: t("recruitment.process.steps.matchmaking.description"),
      icon: "🤝",
      color: "from-slate-800 to-slate-900",
    },
  ];

  const benefits = t("recruitment.benefits.list", { returnObjects: true });

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-24">
      {/* Hero Section */}
      <div className="container mx-auto px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold text-gray-800 mb-4">
            {t("recruitment.title")}
          </h1>
          <div className="w-24 h-1 bg-blue-600 mx-auto mb-6"></div>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            {t("recruitment.hero.description")}
          </p>
        </motion.div>

        {/* Process Section */}
        <div className="py-16">
          <h2 className="text-3xl font-bold text-gray-800 text-center mb-12">
            {t("recruitment.process.title")}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {processSteps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`bg-gradient-to-br ${step.color} rounded-lg p-6 shadow-lg hover:shadow-xl transition-all duration-300`}
              >
                <div className="text-4xl mb-4">{step.icon}</div>
                <h3 className="text-xl font-semibold text-white mb-3">
                  {step.title}
                </h3>
                <p className="text-white">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Benefits Section */}
        <div className="py-16 bg-gray-50 rounded-lg">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">
              {t("recruitment.benefits.title")}
            </h2>
            <div className="w-20 h-1 bg-blue-600 mx-auto mb-6"></div>
            <p className="text-gray-700 max-w-2xl mx-auto">
              {t("recruitment.benefits.description")}
            </p>
            <p className="text-gray-700 max-w-2xl mx-auto mt-4">
              {t("recruitment.benefits.subDescription")}
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 px-4 mx-auto">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                className={`bg-gradient-to-br ${
                  index % 4 === 0
                    ? "from-slate-700 to-slate-800"
                    : index % 4 === 1
                    ? "from-slate-600 to-slate-700"
                    : index % 4 === 2
                    ? "from-slate-800 to-slate-900"
                    : "from-slate-700 to-slate-800"
                } rounded-lg p-4 text-center shadow-lg hover:shadow-xl transition-all duration-300`}
              >
                <p className="text-white font-medium">{benefit}</p>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <motion.a
              href="/contact-us"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors duration-300"
            >
              {t("recruitment.cta.button")}
            </motion.a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecruitmentDetails;
